/**
 * Ecash transfer via Nostr DM (NIP-17 style).
 *
 * Allows wallets to send ecash tokens directly to another user's npub
 * via encrypted Nostr direct messages (kind 14 rumors inside NIP-59 gift wraps).
 */

import type { NostrEvent, EventTemplate } from 'nostr-tools';
import { finalizeEvent, getPublicKey } from 'nostr-tools';
import { hexToBytes } from '@noble/hashes/utils';

import { createSeal, createGiftWrap, unwrapMessage } from './gift-wrap';

/** Payload for an ecash DM */
export interface EcashDMPayload {
  /** The ecash token string (casheroA...) */
  token: string;
  /** Optional message to the recipient */
  message?: string;
  /** The mint's pubkey (so the recipient knows which mint to swap with) */
  mintPubkey: string;
}

/**
 * Send ecash to a user via Nostr DM (NIP-59 gift wrapped kind 14).
 *
 * @param token - The encoded ecash token to send
 * @param mintPubkey - The mint's public key (hex)
 * @param recipientPubkey - Recipient's Nostr public key (hex)
 * @param senderPrivkey - Sender's Nostr private key (hex)
 * @param message - Optional message to include
 * @returns The signed gift wrap event (kind 1059) to publish
 */
export function createEcashDMEvent(
  token: string,
  mintPubkey: string,
  recipientPubkey: string,
  senderPrivkey: string,
  message?: string,
): NostrEvent {
  const payload: EcashDMPayload = {
    token,
    mintPubkey,
    message,
  };

  const senderPrivkeyBytes = hexToBytes(senderPrivkey);
  const senderPubkey = getPublicKey(senderPrivkeyBytes);

  // Create a kind 14 rumor (unsigned direct message)
  const rumor: EventTemplate = {
    kind: 14,
    content: JSON.stringify(payload),
    tags: [['p', recipientPubkey]],
    created_at: Math.floor(Date.now() / 1000),
    pubkey: senderPubkey,
  };

  // Seal it (kind 13) with sender's key
  const seal = createSeal(rumor, senderPrivkey, recipientPubkey);

  // Gift wrap it (kind 1059) with a random key
  const giftWrap = createGiftWrap(seal, recipientPubkey);

  return giftWrap;
}

/**
 * Parse an ecash DM event (unwrap a gift wrap to get the token).
 *
 * @param giftWrapEvent - The kind 1059 gift wrap event
 * @param recipientPrivkey - Recipient's Nostr private key (hex)
 * @returns The ecash token and sender info, or null if invalid
 */
export function parseEcashDMEvent(
  giftWrapEvent: NostrEvent,
  recipientPrivkey: string,
): { token: string; senderPubkey: string; mintPubkey: string; message?: string } | null {
  try {
    // Unwrap gift wrap → seal → rumor
    const { rumor, senderPubkey } = unwrapMessage(giftWrapEvent, recipientPrivkey);

    // Verify it's a kind 14 message
    if (rumor.kind !== 14) return null;

    // Parse the rumor content as our ecash payload
    const payload: EcashDMPayload = JSON.parse(rumor.content);

    if (!payload.token || !payload.mintPubkey) return null;

    return {
      token: payload.token,
      senderPubkey,
      mintPubkey: payload.mintPubkey,
      message: payload.message,
    };
  } catch {
    return null;
  }
}

/**
 * Get the kind 10050 (DM relay list) filter for a user.
 */
export function getDMRelayListFilter(pubkey: string) {
  return {
    kinds: [10050],
    authors: [pubkey],
    limit: 1,
  };
}
